import React, { useState, useRef, useEffect } from 'react';
import './Results.css';

// Strip conversational meta-intro (e.g. "Sarah, I've got a clear picture...") but preserve the
// opening paragraph ("[Name], your Personal IP is..." or "[Name], you're at the intersection of...")
const INTRO_PRESERVE_CHARS = 250;

function stripIntro(content) {
  if (!content || typeof content !== 'string') return content || '';
  const markers = [
    /\byour\s+personal\s+ip\b/i,
    /\*\*your\s+personal\s+ip\*\*/i,
    /personal\s+ip\s+summary/i,
    /^\s*\d+\.\s+.+/m,
    /^\s*\*\*[^*]+(?:tool|coach|generator|assistant|builder)[^*]*\*\*/im
  ];
  let earliestIdx = content.length;
  for (const re of markers) {
    const match = content.match(re);
    if (match) {
      const idx = content.indexOf(match[0]);
      if (idx >= 0 && idx < earliestIdx) earliestIdx = idx;
    }
  }
  // Preserve first ~250 chars so intro paragraph is never stripped
  if (earliestIdx > 0 && earliestIdx < INTRO_PRESERVE_CHARS) {
    earliestIdx = 0;
  }
  if (earliestIdx < content.length) {
    return content.slice(earliestIdx).trim();
  }
  return content.trim();
}

// Parse synthesis into: (1) Personal IP summary, (2) one section per tool
// Only split on **Tool name** or 1. Tool name - do NOT split on **What it does:** etc.
function parseSynthesis(content) {
  const stripped = stripIntro(content);
  if (!stripped) return [{ title: 'Your Personal IP Report', content: content || '' }];

  const items = [];
  const lines = stripped.split('\n');
  let currentItem = null;
  let buffer = [];

  const LEVELS = ['simple', 'medium', 'advanced'];

  const parseLevel = (s) => {
    const m = s.match(/\s*\[(simple|medium|advanced)\]\s*$/i);
    return m ? m[1].toLowerCase() : null;
  };

  const stripLevel = (s) => (s || '').replace(/\s*\[(simple|medium|advanced)\]\s*$/i, '').trim();

  const flushBuffer = (title, level) => {
    const text = buffer.join('\n').trim();
    if (text) {
      items.push({ title: title || (items.length === 0 ? 'Your Personal IP' : 'Section'), content: text, level: level || null });
    }
    buffer = [];
  };

  const hasLevelTag = (s) => /\[(simple|medium|advanced)\]/i.test(s || '');

  const isToolStart = (line) => {
    // ## **Tool Name** [simple] or ## 1. Tool Name (markdown headings)
    const headingBold = line.match(/^\s*#+\s*\*\*([^*]+)\*\*:?\s*(.*)$/);
    if (headingBold && headingBold[1].trim().length > 2) {
      const keywordOk = /tool|coach|generator|assistant|builder/i.test(headingBold[1]) || hasLevelTag(line);
      if (keywordOk) {
        const raw = headingBold[1].trim().slice(0, 80);
        return { title: stripLevel(raw), level: parseLevel(headingBold[1]) || null, rest: headingBold[2]?.trim() || null };
      }
    }
    const headingNum = line.match(/^\s*#+\s*(\d+)\.\s+(.+)/);
    if (headingNum && headingNum[2].trim().length > 2) {
      const raw = headingNum[2].trim().slice(0, 80);
      return { title: stripLevel(raw), level: parseLevel(headingNum[2]) || null, rest: null };
    }
    // **Tool name** [simple] or 1. Tool name (no heading)
    const numMatch = line.match(/^(\d+)\.\s+(.+)/);
    if (numMatch && numMatch[2].trim().length > 2) {
      const raw = numMatch[2].trim().slice(0, 80);
      return { title: stripLevel(raw), level: parseLevel(numMatch[2]) || null, rest: null };
    }
    const boldMatch = line.match(/^\*\*([^*]+)\*\*\s*$/);
    if (boldMatch && boldMatch[1].trim().length > 2) {
      const raw = boldMatch[1].trim().slice(0, 80);
      return { title: stripLevel(raw), level: parseLevel(boldMatch[1]) || null, rest: null };
    }
    const boldInline = line.match(/^\*\*([^*]+)\*\*:?\s*(.*)/);
    if (boldInline && boldInline[1].trim().length > 2) {
      const keywordOk = /tool|coach|generator|assistant|builder/i.test(boldInline[1]) || hasLevelTag(line);
      if (keywordOk) {
        const raw = boldInline[1].trim().slice(0, 80);
        return { title: stripLevel(raw), level: parseLevel(boldInline[1]) || null, rest: boldInline[2] };
      }
    }
    return null;
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const toolStart = isToolStart(line);

    if (toolStart) {
      flushBuffer(currentItem?.title, currentItem?.level);
      currentItem = { title: toolStart.title, content: '', level: toolStart.level };
      buffer = toolStart.rest ? [toolStart.rest] : [];
    } else {
      buffer.push(line);
    }
  }

  flushBuffer(currentItem?.title, currentItem?.level);

  if (items.length === 0) {
    return [{ title: 'Your Personal IP Report', content: stripped }];
  }

  // Strip "Your Personal IP" from first section content when title already says it
  if (items[0]?.title === 'Your Personal IP' && items[0].content) {
    items[0].content = items[0].content.replace(/^\s*(Your\s+)?Personal\s+IP\b:?\s*/i, '').trim();
  }

  // Assign [simple], [medium], [advanced] by position for tool items (1, 2, 3)
  // Always use position so we enforce: first tool=simple, second=medium, third=advanced
  for (let i = 1; i <= 3 && i < items.length; i++) {
    items[i].level = LEVELS[i - 1];
  }

  // Sort tool items (those with level) by simple -> medium -> advanced so display order is correct
  const introItems = items.filter((item) => !item.level);
  const toolItems = items.filter((item) => item.level);
  toolItems.sort((a, b) => {
    const ai = LEVELS.indexOf(a.level);
    const bi = LEVELS.indexOf(b.level);
    return (ai >= 0 ? ai : 999) - (bi >= 0 ? bi : 999);
  });
  return [...introItems, ...toolItems];
}

// Split content into text vs starter-prompt blocks. Strip backticks; starter prompts render as plain text with scroll.
function formatReportContent(content) {
  if (!content || typeof content !== 'string') return content;
  let text = content.replace(/`{4,}/g, '```');
  const parts = [];
  const codeBlockRe = /```([\s\S]*?)```/g;
  let lastIdx = 0;
  let m;
  while ((m = codeBlockRe.exec(text)) !== null) {
    if (m.index > lastIdx) {
      const before = stripBackticks(text.slice(lastIdx, m.index).trim());
      if (before) parts.push({ type: 'text', content: before });
    }
    const inner = stripBackticks(m[1].trim());
    if (inner) parts.push({ type: 'text', content: inner }); // treat as plain text, not code block
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < text.length) {
    const after = stripBackticks(text.slice(lastIdx).trim());
    if (after) parts.push({ type: 'text', content: after });
  }
  return parts;
}

function stripBackticks(s) {
  if (!s || typeof s !== 'string') return s || '';
  return s.replace(/^`+/, '').replace(/`+$/, '').trim();
}

// Normalize prompt content: convert literal \n and \t to actual newlines/tabs (fixes JSON-escaped strings)
function normalizePromptContent(str) {
  if (!str || typeof str !== 'string') return str;
  return str.replace(/\\n/g, '\n').replace(/\\t/g, '\t');
}

// Check if synthesisContent is new adaptive format (object) or legacy (string)
function isAdaptiveFormat(content) {
  return content && typeof content === 'object' && typeof content.synthesis === 'string' && content.prompt && typeof content.prompt.content === 'string';
}

// If content is a stringified JSON object (adaptive format), parse it
function normalizeSynthesisContent(content) {
  if (!content) return content;
  if (typeof content === 'object') return content;
  if (typeof content !== 'string') return content;
  const trimmed = content.trim();
  if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed.synthesis === 'string' && parsed.prompt && typeof parsed.prompt.content === 'string') {
        return parsed;
      }
    } catch (e) { /* fall through to return original */ }
  }
  return content;
}

const PHILOSOPHY_STATEMENT = 'We believe in personal empowerment in the AI era—that happens when you combine your unique expertise with the right tools to give you superpowers.';

function Results({ synthesisContent, conversationHistory, sessionState, onEmailSent }) {
  const [step, setStep] = useState('results'); // 'results' | 'email' | 'confirm'
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [reachOutForChat, setReachOutForChat] = useState(false);
  const [isPromptExpanded, setIsPromptExpanded] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const resultsContainerRef = useRef(null);

  const normalizedContent = normalizeSynthesisContent(synthesisContent);
  const isNewFormat = isAdaptiveFormat(normalizedContent);

  // Scroll to top when report loads so user sees philosophy, synthesis, and accordion header first
  useEffect(() => {
    if (!synthesisContent) return;
    const el = resultsContainerRef.current;
    if (el) {
      const scroll = () => { el.scrollTop = 0; };
      scroll();
      requestAnimationFrame(scroll); // After paint, in case layout wasn't ready
    }
  }, [synthesisContent]);
  const synthesis = isNewFormat ? normalizePromptContent(normalizedContent.synthesis) : null;
  const prompt = isNewFormat ? normalizedContent.prompt : null;

  const items = isNewFormat ? null : parseSynthesis(normalizedContent).slice(0, 4);
  const firstItem = items?.[0];
  const isSingleParagraph = firstItem?.title === 'Your Personal IP' && firstItem?.content && !firstItem.content.includes('\n\n');
  const [expandedIndex, setExpandedIndex] = useState(isSingleParagraph ? 0 : -1);

  const toggleExpand = (idx) => {
    setExpandedIndex(expandedIndex === idx ? -1 : idx);
  };

  const copyToClipboard = async () => {
    if (!prompt?.content) return;
    try {
      await navigator.clipboard.writeText(normalizePromptContent(prompt.content));
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      alert('Failed to copy. Please select and copy manually.');
    }
  };

  const handleGetReport = () => {
    setStep('email');
    setEmailError(null);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!trimmed || !emailRegex.test(trimmed)) {
      setEmailError('That doesn\'t look like a valid email. Please try again.');
      return;
    }

    setIsSending(true);
    setEmailError(null);

    try {
      const response = await fetch('/api/send-report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: trimmed,
          interested_in_chatting: reachOutForChat,
          synthesisData: {
            synthesis: normalizedContent,
            conversationHistory: conversationHistory,
            sessionId: sessionState?.sessionId
          }
        })
      });

      const contentType = response.headers.get('content-type');
      const text = await response.text();

      if (!response.ok) {
        let errorMsg = 'We couldn\'t send the report. Please try again. If it keeps failing, email us at hello@xf.works.';
        if (contentType && contentType.includes('application/json')) {
          try {
            const errorData = JSON.parse(text);
            errorMsg = errorData.error || errorMsg;
          } catch (e) { /* use default */ }
        }
        throw new Error(errorMsg);
      }

      setStep('confirm');
      try {
        if (typeof window !== 'undefined' && window.gtag) {
          window.gtag('event', 'email_sent');
        }
      } catch (e) { /* Firefox iOS may throw from gtag */ }
      onEmailSent?.();
    } catch (err) {
      setEmailError(err.message || 'We couldn\'t send the report. Please try again. If it keeps failing, email us at hello@xf.works.');
    } finally {
      setIsSending(false);
    }
  };

  if (step === 'confirm') {
    return (
      <div className="results-container">
        <div className="terminal-frame results-frame">
          <div className="terminal-titlebar">XF - Personal IP Discovery Tool</div>
          <div className="terminal-content results-content">
            <p className="text-primary text-center">
              Report sent. Check your inbox.
            </p>
            <p className="text-muted text-center mt-3">
              For more, find us at <a href="https://xf.works" target="_blank" rel="noopener noreferrer">xf.works</a>. Learn about the Personal IP framework at <a href="https://intentional.tech" target="_blank" rel="noopener noreferrer">intentional.tech</a>.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'email') {
    return (
      <div className="results-container">
        <div className="terminal-frame results-frame">
          <div className="terminal-titlebar">XF - Personal IP Discovery Tool</div>
          <div className="terminal-content results-content">
            <p className="text-content mb-3">
              Enter your email to receive the report.
            </p>
            <form onSubmit={handleEmailSubmit} className="results-email-form">
              <label htmlFor="email-input" className="visually-hidden">
                Enter your email address to receive your Personal IP report
              </label>
              <input
                id="email-input"
                type="email"
                className="terminal-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your email address"
                disabled={isSending}
                autoComplete="email"
                aria-required="true"
                aria-label="Your email address for receiving the Personal IP report"
              />
              <div className="results-checkboxes mt-2">
                <label className="results-checkbox">
                  <input
                    type="checkbox"
                    checked={reachOutForChat}
                    onChange={(e) => setReachOutForChat(e.target.checked)}
                    disabled={isSending}
                  />
                  <span className="text-muted">I'm interested in chatting about AI and transformation</span>
                </label>
              </div>
              {emailError && (
                <div className="error-box">
                  <span className="text-error">error:</span>
                  <div className="message-content text-error">{emailError}</div>
                </div>
              )}
              <div className="results-email-actions mt-3">
                <button
                  type="button"
                  className="terminal-button"
                  onClick={() => setStep('results')}
                  disabled={isSending}
                >
                  [back]
                </button>
                <button
                  type="submit"
                  className="terminal-button"
                  disabled={isSending}
                >
                  {isSending ? '[sending...]' : '[send report]'}
                </button>
              </div>
            </form>
            <p className="text-muted mt-3" style={{ fontSize: '13px' }}>
              Questions? <a href="mailto:hello@xf.works">hello@xf.works</a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  const isTestMode = sessionState?.sessionId === 'test-results';

  // New adaptive format: philosophy + synthesis + prompt accordion
  if (isNewFormat) {
    return (
      <div className="results-container" ref={resultsContainerRef}>
        <div className="terminal-frame results-frame">
          <div className="terminal-titlebar" style={isTestMode ? { display: 'flex', justifyContent: 'space-between', alignItems: 'center' } : undefined}>
            XF - Personal IP Discovery Tool
            {isTestMode && (
              <a href="/" className="text-muted" style={{ fontSize: '11px', textDecoration: 'none' }}>
                ← Back to start
              </a>
            )}
          </div>
          <div className="terminal-content results-content">
            <div className="philosophy-statement">{PHILOSOPHY_STATEMENT}</div>

            <div className="synthesis-section">
              <h2 className="results-section-h2">YOUR PERSONAL IP</h2>
              <p className="synthesis-text">{normalizePromptContent(synthesis)}</p>
            </div>

            <div className="prompt-section">
              <h2 className="results-section-h2">YOUR PERSONALIZED PROMPT</h2>
              <div className={`prompt-accordion ${isPromptExpanded ? 'expanded' : 'collapsed'}`}>
                <div
                  className="prompt-header"
                  onClick={() => setIsPromptExpanded(!isPromptExpanded)}
                  onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), setIsPromptExpanded(!isPromptExpanded))}
                  role="button"
                  tabIndex={0}
                  aria-expanded={isPromptExpanded}
                  aria-label={isPromptExpanded ? 'Collapse prompt' : 'Expand to view and copy prompt'}
                >
                  <span className="expand-icon">{isPromptExpanded ? '▾' : '▸'}</span>
                  <div className="prompt-title-section">
                    <h3 className="prompt-title">{prompt.title}</h3>
                    <p className="platform-info">Platform: {prompt.platform}</p>
                    {!isPromptExpanded && <span className="prompt-expand-hint">Click to expand</span>}
                  </div>
                  <span className="prompt-copy-reminder">Copy the prompt below to use it</span>
                </div>

                {isPromptExpanded && (
                  <div className="prompt-content">
                    <div className="prompt-body">
                      <pre>{normalizePromptContent(prompt.content)}</pre>
                    </div>
                    <div className="prompt-actions">
                      <button
                        type="button"
                        className="terminal-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard();
                        }}
                      >
                        {copySuccess ? '[copied!]' : '[copy prompt]'}
                      </button>
                      <p className="usage-note">{prompt.usage_note}</p>
                    </div>
                    <p className="length-warning">
                      Note: This prompt is detailed (about {Math.round((normalizePromptContent(prompt.content) || '').split(/\s+/).length)} words).
                      Paste it into {prompt.platform} to get started.
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="results-actions mt-4">
              <button type="button" className="terminal-button" onClick={handleGetReport}>
                [email report]
              </button>
            </div>
            <p className="text-muted mt-3" style={{ fontSize: '13px' }}>
              Questions? <a href="mailto:hello@xf.works">hello@xf.works</a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Legacy format: accordion list of tools
  return (
    <div className="results-container" ref={resultsContainerRef}>
      <div className="terminal-frame results-frame">
        <div className="terminal-titlebar">XF - Personal IP Discovery Tool</div>
        <div className="terminal-content results-content">
          <p className="text-primary mb-3">Your Personal IP Report</p>
          <p className="text-muted mb-3" style={{ fontSize: '14px' }}>
            Click to expand each section.
          </p>

          {items.length > 0 && !items[0].level && (
            <div className="results-intro mb-4">
              {formatReportContent(items[0].content).map((part, i) =>
                part.content ? (
                  <pre key={i} className="results-item-pre">{part.content}</pre>
                ) : null
              )}
            </div>
          )}

          <div className="results-list">
            {items
              .filter((item, idx) => idx > 0 || item.level)
              .map((item, idx) => {
                const originalIdx = items.findIndex((x) => x === item);
                return (
                  <div key={originalIdx} className="results-item">
                    <button
                      type="button"
                      className="results-item-header"
                      onClick={() => toggleExpand(originalIdx)}
                      aria-expanded={expandedIndex === originalIdx}
                      aria-label={`${expandedIndex === originalIdx ? 'Collapse' : 'Expand'} ${item.title} section`}
                    >
                      <span className="results-item-title">{item.title}{item.level ? ` [${item.level}]` : ''}</span>
                      <span className="results-item-toggle">{expandedIndex === originalIdx ? '−' : '+'}</span>
                    </button>
                    {expandedIndex === originalIdx && (
                      <div className="results-item-content">
                        {formatReportContent(item.content).map((part, i) =>
                          part.content ? (
                            <pre key={i} className="results-item-pre results-item-pre-scroll">{part.content}</pre>
                          ) : null
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>

          <div className="results-actions mt-4">
            <button type="button" className="terminal-button" onClick={handleGetReport}>
              [email report]
            </button>
          </div>
          <p className="text-muted mt-3" style={{ fontSize: '13px' }}>
            Questions? <a href="mailto:hello@xf.works">hello@xf.works</a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Results;
